/**
 * InvestSignals — hero infographic for the Why page.
 * A VC-pitch-deck style panel showing four investment thesis signals
 * with animated fill bars and an "opportunity window" status chip.
 * Designed for transparent background on the #0EA5E9 sky-blue hero.
 */

const SIGNALS = [
  {
    label: 'Market timing',
    detail: 'AI governance urgently needed — now',
    pct: 91,
    color: '#ffffff',
  },
  {
    label: 'Problem scale',
    detail: 'Enterprise-wide operational risk',
    pct: 88,
    color: '#4AAC00',
  },
  {
    label: 'Competitive moat',
    detail: 'Proprietary operating method — built first',
    pct: 82,
    color: '#FF8C00',
  },
  {
    label: 'Team depth',
    detail: 'Operator-founded, governance-first',
    pct: 85,
    color: '#ffffff',
  },
];

export function InvestSignals() {
  return (
    <div className="is-wrap">
      <style>{`
        .is-wrap {
          width: 100%;
          max-width: 380px;
          display: flex;
          flex-direction: column;
          gap: 0;
          padding: 4px 0;
        }

        .is-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
        }

        .is-header-label {
          font-size: 10px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.55);
        }

        .is-live {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 10px;
          color: rgba(255,255,255,0.55);
          letter-spacing: 0.08em;
        }

        .is-live-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #4AAC00;
          animation: is-pulse 2s ease-in-out infinite;
        }

        @keyframes is-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.75); }
        }

        .is-row {
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding: 10px 12px;
          border-radius: 10px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.14);
          margin-bottom: 7px;
          opacity: 0;
          transform: translateX(16px);
        }

        .is-row-0 { animation: is-slide-in 0.45s ease-out 0.15s forwards; }
        .is-row-1 { animation: is-slide-in 0.45s ease-out 0.30s forwards; }
        .is-row-2 { animation: is-slide-in 0.45s ease-out 0.45s forwards; }
        .is-row-3 { animation: is-slide-in 0.45s ease-out 0.60s forwards; }

        @keyframes is-slide-in {
          to { opacity: 1; transform: translateX(0); }
        }

        .is-row-top {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
        }

        .is-row-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.9);
        }

        .is-row-detail {
          font-size: 10px;
          color: rgba(255,255,255,0.5);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .is-row-pct {
          font-size: 10px;
          font-weight: 700;
          color: rgba(255,255,255,0.6);
          flex-shrink: 0;
        }

        .is-track {
          height: 4px;
          border-radius: 4px;
          background: rgba(255,255,255,0.12);
          overflow: hidden;
        }

        .is-fill {
          height: 100%;
          border-radius: 4px;
          width: 0%;
          transition: none;
        }

        .is-fill-0 { animation: is-fill-0 0.9s cubic-bezier(0.22,1,0.36,1) 0.5s forwards; }
        .is-fill-1 { animation: is-fill-1 0.9s cubic-bezier(0.22,1,0.36,1) 0.65s forwards; }
        .is-fill-2 { animation: is-fill-2 0.9s cubic-bezier(0.22,1,0.36,1) 0.80s forwards; }
        .is-fill-3 { animation: is-fill-3 0.9s cubic-bezier(0.22,1,0.36,1) 0.95s forwards; }

        @keyframes is-fill-0 { to { width: 91%; } }
        @keyframes is-fill-1 { to { width: 88%; } }
        @keyframes is-fill-2 { to { width: 82%; } }
        @keyframes is-fill-3 { to { width: 85%; } }

        .is-window {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-top: 4px;
          padding: 10px 14px;
          border-radius: 10px;
          border: 1.5px solid rgba(74,172,0,0.5);
          background: rgba(74,172,0,0.10);
          opacity: 0;
          animation: is-slide-in 0.45s ease-out 0.85s forwards;
        }

        .is-window-label {
          font-size: 10px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.8);
          font-weight: 600;
        }

        .is-window-status {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.1em;
          color: #4AAC00;
          text-transform: uppercase;
        }

        @media (prefers-reduced-motion: reduce) {
          .is-row,
          .is-window {
            animation: none;
            opacity: 1;
            transform: none;
          }
          .is-fill-0 { animation: none; width: 91%; }
          .is-fill-1 { animation: none; width: 88%; }
          .is-fill-2 { animation: none; width: 82%; }
          .is-fill-3 { animation: none; width: 85%; }
          .is-live-dot { animation: none; }
        }
      `}</style>

      <div className="is-header">
        <span className="is-header-label">Investment signals</span>
        <span className="is-live">
          <span className="is-live-dot" />
          Live analysis
        </span>
      </div>

      {SIGNALS.map(({ label, detail, pct, color }, i) => (
        <div key={label} className={`is-row is-row-${i}`}>
          <div className="is-row-top">
            <span className="is-row-label">{label}</span>
            <span className="is-row-pct">{pct}%</span>
          </div>
          <span className="is-row-detail">{detail}</span>
          <div className="is-track">
            <div
              className={`is-fill is-fill-${i}`}
              style={{ background: color === '#ffffff' ? 'rgba(255,255,255,0.75)' : color }}
            />
          </div>
        </div>
      ))}

      <div className="is-window">
        <span className="is-window-label">Opportunity window</span>
        <span className="is-window-status">Open</span>
      </div>
    </div>
  );
}
